import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// region External libraries
import { Autocomplete, Button, IconButton, TextField } from "@libraries/mui/components";
import { Delete } from "@libraries/mui/icons";
// endregion External libraries
// region Atoms
import StateMessage from "@atoms/StateMessage";
// endregion Atoms
// region Molecules
import ModalFormDialog from "@molecules/ModalFormDialog";
// endregion Molecules
// region Hooks
import { useAuth } from "@hooks/useAuth";
// endregion Hooks
// region Languages
import useTranslation from "@languages/useTranslation";
// endregion Languages

import { LegacyMigrationMessages } from "./languages/interfaces";
import { translateError } from "./messages";
import legacyMigrationService, {
  LegacyMigrationError,
  LegacyMigrationManager,
  LegacyMigrationManagerCandidate,
  toLegacyMigrationError
} from "./service";
import * as Styled from "./styles";

interface IAccessDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Who may use the legacy migration: the holders of the LEGACY_MIGRATION staff
 * permission, who can give it to another admin or take it away.
 *
 * The migration's counterpart of the announcement managers dialog, fed by the
 * migration's own REST routes. Refusals (not an admin, removing your own
 * access) come from the server and are shown as they are — the screen does not
 * guess them.
 */
const AccessDialog: React.FC<IAccessDialogProps> = ({ open, onClose }) => {

  // region Hooks
  const { t } = useTranslation();
  const { getToken } = useAuth();
  // endregion Hooks
  // region States
  const [managers, setManagers] = useState<LegacyMigrationManager[]>([]);
  const [candidates, setCandidates] = useState<LegacyMigrationManagerCandidate[]>([]);
  const [picked, setPicked] = useState<LegacyMigrationManagerCandidate | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<LegacyMigrationError | null>(null);
  // endregion States

  const service = useMemo(() => legacyMigrationService(getToken), [getToken]);
  const mounted = useRef<boolean>(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const [managerList, candidateList] = await Promise.all([
        service.listManagers(),
        service.listManagerCandidates()
      ]);

      if (!mounted.current) return;

      setManagers(managerList);
      setCandidates(candidateList);
    } catch (rejection) {
      if (mounted.current) setError(toLegacyMigrationError(rejection));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    if (!open) return;

    setError(null);
    setPicked(null);
    load();
  }, [open, load]);

  /** Runs a grant or a revoke and reloads both lists; false when the server refused it. */
  const act = async (action: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();

      return true;
    } catch (rejection) {
      if (mounted.current) setError(toLegacyMigrationError(rejection));

      return false;
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const grant = async () => {
    if (!picked) return;

    if (await act(() => service.grantAccess(picked.userId))) setPicked(null);
  };

  return (
    <ModalFormDialog
      open={open}
      title={t(LegacyMigrationMessages.legacyMigrationAccessTitle)}
      subtitle1={t(LegacyMigrationMessages.legacyMigrationAccessSubtitle)}
      onClose={onClose}
    >
      <Styled.AccessContent>
        {error && <Styled.Banner $tone="error">{translateError(t, error.errorCode)}</Styled.Banner>}

        <Styled.GrantRow>
          <Autocomplete
            options={candidates}
            value={picked}
            onChange={(_event, value) => setPicked(value)}
            getOptionLabel={(option) => `${option.userName} (${option.userEmail})`}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            size="small"
            fullWidth
            disabled={busy}
            renderInput={(params) => (
              <TextField {...params} label={t(LegacyMigrationMessages.legacyMigrationAccessGrantLabel)} size="small" />
            )}
          />
          <Button variant="contained" disabled={busy || !picked} onClick={grant}>
            {t(LegacyMigrationMessages.legacyMigrationAccessGrant)}
          </Button>
        </Styled.GrantRow>

        {loading && <StateMessage>{t(LegacyMigrationMessages.legacyMigrationAccessLoading)}</StateMessage>}

        {!loading && managers.length === 0 && (
          <StateMessage>{t(LegacyMigrationMessages.legacyMigrationAccessEmpty)}</StateMessage>
        )}

        {!loading && managers.map((manager) => (
          <Styled.ManagerRow key={manager.userId}>
            <Styled.ManagerInfo>
              <Styled.ManagerName>{manager.userName}</Styled.ManagerName>
              <Styled.ManagerEmail>{manager.userEmail}</Styled.ManagerEmail>
              {manager.grantedByName && (
                <Styled.GrantedBy>
                  {t(LegacyMigrationMessages.legacyMigrationAccessGrantedBy, {
                    name: manager.grantedByName,
                    date: new Date(manager.grantedAt).toLocaleDateString()
                  })}
                </Styled.GrantedBy>
              )}
            </Styled.ManagerInfo>
            <IconButton
              aria-label={String(t(LegacyMigrationMessages.legacyMigrationAccessRevoke))}
              title={String(t(LegacyMigrationMessages.legacyMigrationAccessRevoke))}
              size="small"
              disabled={busy}
              onClick={() => act(() => service.revokeAccess(manager.userId))}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Styled.ManagerRow>
        ))}
      </Styled.AccessContent>
    </ModalFormDialog>
  );
};

export default AccessDialog;
